import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  IconButton,
  Badge,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Checkbox
} from '@mui/material';
import {
  History,
  Folder,
  Add,
  Delete,
  Edit,
  AttachFile,
  Link,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  Close,
  ChatBubble,
  Extension,
  LinkedIn,
  Message,
  BugReport,
  CloudQueue,
  Storage,
  Logout,
  Dashboard,
  Search,
  Settings,
  Brightness4,
  Brightness7,
  ArrowBack
} from '@mui/icons-material';
import { getSessions, deleteSessions, updateSession } from './services/sessionService';
import { deleteFiles } from './services/fileService';
import { useUser } from '../../contexts/PioAgentUserContext';
import { useTheme } from './context/ThemeContext';
import ConfirmDialog from './ConfirmDialog';
import { useNavigate } from 'react-router-dom';

const Sidebar = ({
  isOpen,
  documents,
  totalDocCount,
  uploadLoading,
  isLoadingMoreDocs,
  hasMorePages,
  onDeleteDocument,
  onFileUpload,
  fileInputRef,
  onCrawl,
  crawlUrlInput,
  setCrawlUrlInput,
  crawlLoading,
  crawlStatus,
  onSessionSelect,
  currentSessionId,
  onNewSession,
  onLogout,
  onSelectAgents,
  userRole,
  onRefreshSessions,
  documentsEndRef,
  onLoadDocuments,
  onLoadMore
}) => {
  const { apiRole, apiConfigured, apiKey } = useUser();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Check if we came from AgentStudio
  const fromAgentStudio = sessionStorage.getItem('pioagent_agent_context') !== null;

  const handleBackToAgentStudio = () => {
    // Clear agent context
    sessionStorage.removeItem('pioagent_agent_context');
    sessionStorage.removeItem('pioagent_current_agent');
    navigate('/agent-studio');
  };
  const [expandedSection, setExpandedSection] = useState('history');
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [crawlDialogOpen, setCrawlDialogOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [crawlOptions, setCrawlOptions] = useState({
    maxPage: 100,
    maxDepth: 3,
    crawlSubDomain: true,
    fetchFiles: false
  });
  const [sessionSelectionMode, setSessionSelectionMode] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [documentSelectionMode, setDocumentSelectionMode] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sessionPage, setSessionPage] = useState(1);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [sessionToRename, setSessionToRename] = useState(null);
  const [newSessionName, setNewSessionName] = useState('');
  const [renamingSession, setRenamingSession] = useState(false);

  useEffect(() => {
    if (isOpen && expandedSection === 'history' && apiConfigured && apiKey) {
      loadSessions(1, true);
    }
  }, [isOpen, expandedSection, apiConfigured, apiKey]);

  useEffect(() => {
    if (onRefreshSessions && typeof onRefreshSessions === 'function') {
      onRefreshSessions(() => loadSessions(1, true));
    }
  }, [onRefreshSessions]);

  const loadSessions = async (page = 1, isInitialLoad = true) => {
    if (!apiKey) {
      return;
    }

    try {
      if (isInitialLoad) {
    setLoadingSessions(true);
        setSessions([]);
        setSessionPage(1);
        setHasMoreSessions(true);
      } else {
        setIsLoadingMoreSessions(true);
      }

      const response = await getSessions(page, 30);
      if (response?.data?.sessions) {
        const newSessions = response.data.sessions;
        
        if (newSessions.length === 0) {
          setHasMoreSessions(false);
        } else {
          setSessionPage(page);
          setSessions(prev => isInitialLoad ? newSessions : [...prev, ...newSessions]);
        }
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
      setHasMoreSessions(false);
    } finally {
      setLoadingSessions(false);
      setIsLoadingMoreSessions(false);
    }
  };

  const handleSectionToggle = (section) => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }

    // Only admin users can access documents and integrations sections
    if ((section === 'documents' || section === 'plugins') && !isAdmin) {
      return;
    }

    setExpandedSection(expandedSection === section ? null : section);
  };

  const formatModelName = (modelKey) => {
    const modelName = modelKey.split('/').pop() || modelKey;
    return modelName
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isAdmin = apiRole === 'admin';
  const collapsedWidth = 65;
  const expandedWidth = 300;
  const sidebarWidth = isOpen ? (isCollapsed ? collapsedWidth : expandedWidth) : 0;

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    if (!isCollapsed) {
      setExpandedSection(null);
    }
  };

  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  const selectAllSessions = () => {
    setSelectedSessions(sessions.map(s => s.session_id));
  };

  const deselectAllSessions = () => {
    setSelectedSessions([]);
  };

  const toggleDocumentSelection = (docId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const selectAllDocuments = () => {
    setSelectedDocuments(documents.map(doc => doc.id));
  };

  const deselectAllDocuments = () => {
    setSelectedDocuments([]);
  };

  const handleRenameClick = (session) => {
    setSessionToRename(session);
    setNewSessionName(session.title || '');
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = async () => {
    if (!sessionToRename || !newSessionName.trim()) {
      return;
    }

    setRenamingSession(true);
    try {
      await updateSession(sessionToRename.session_id, newSessionName.trim(), sessionToRename.files || []);
      await loadSessions(1, true);
      setRenameDialogOpen(false);
      setSessionToRename(null);
      setNewSessionName('');
    } catch (error) {
      console.error('Failed to rename session:', error);
    } finally {
      setRenamingSession(false);
    }
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setSessionToRename(null);
    setNewSessionName('');
  };

  const handleDeleteClick = (type, id = null) => {
    setDeleteTarget({ type, id });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      if (deleteTarget.type === 'sessions') {
        if (deleteTarget.id) {
          await deleteSessions([deleteTarget.id]);
          await loadSessions(1, true);
        } else {
          await deleteSessions(selectedSessions);
          await loadSessions(1, true);
          setSelectedSessions([]);
          setSessionSelectionMode(false);
        }
      } else if (deleteTarget.type === 'documents') {
        if (deleteTarget.id) {
          await onDeleteDocument(deleteTarget.id);
        } else {
          await deleteFiles(selectedDocuments);
          if (onLoadDocuments) {
            await onLoadDocuments();
          }
          setSelectedDocuments([]);
          setDocumentSelectionMode(false);
        }
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const getDeleteMessage = () => {
    if (!deleteTarget) return '';
    if (deleteTarget.type === 'sessions') {
      if (deleteTarget.id) return 'Are you sure you want to delete this session? This action cannot be undone.';
      return `Are you sure you want to delete ${selectedSessions.length} session${selectedSessions.length > 1 ? 's' : ''}? This action cannot be undone.`;
    } else if (deleteTarget.type === 'documents') {
      if (deleteTarget.id) return 'Are you sure you want to delete this document? This action cannot be undone.';
      return `Are you sure you want to delete ${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''}? This action cannot be undone.`;
    }
    return '';
  };

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        width: sidebarWidth,
        transition: 'width 0.3s ease',
        overflow: 'hidden',
        position: 'relative',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh'
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src="/WhiteLabelLogos/ut_long.png"
              alt="University of Twente Logo"
              style={{ 
                height: isCollapsed ? '32px' : '36px', 
                width: 'auto',
                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none'
              }}
            />
          </Box>
          {!isCollapsed && (
            <IconButton 
              onClick={handleToggleCollapse} 
              size="small"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          )}
          {isCollapsed && (
            <IconButton 
              onClick={handleToggleCollapse} 
              size="small"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {!isCollapsed && (
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="contained"
            startIcon={<Add fontSize="small" />}
            onClick={onNewSession}
            fullWidth
            size="small"
            sx={{ height: 36, fontSize: '0.9rem' }}
          >
            New Chat
          </Button>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {isCollapsed ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.2, gap: 1.2 }}>
            <Tooltip title="New Chat" placement="right">
              <IconButton 
                onClick={onNewSession}
                size="medium"
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ height: 1, bgcolor: 'divider', width: '60%', my: 0.5 }} />
            <Tooltip title="Chat History" placement="right">
              <IconButton 
                onClick={() => handleSectionToggle('history')}
                size="medium"
                sx={{ 
                  bgcolor: expandedSection === 'history' ? 'primary.lighter' : 'transparent',
                  '&:hover': { bgcolor: 'primary.lighter' }
                }}
              >
                <History fontSize="small" />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <>
            <Tooltip title="Documents" placement="right">
              <IconButton
                onClick={() => handleSectionToggle('documents')}
                size="medium"
                sx={{
                  bgcolor: expandedSection === 'documents' ? 'primary.lighter' : 'transparent',
                  '&:hover': { bgcolor: 'primary.lighter' },
                      position: 'relative'
                }}
              >
                <Storage fontSize="small" />
                    {totalDocCount > 0 && (
                  <Badge
                    badgeContent={totalDocCount}
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: '18px', height: '18px' }
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Integrations" placement="right">
              <IconButton
                onClick={() => handleSectionToggle('plugins')}
                size="medium"
                sx={{
                  bgcolor: expandedSection === 'plugins' ? 'primary.lighter' : 'transparent',
                      '&:hover': { bgcolor: 'primary.lighter' }
                }}
              >
                <Extension fontSize="small" />
              </IconButton>
            </Tooltip>
              </>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Toggle Theme" placement="right">
              <IconButton onClick={toggleTheme} size="medium" sx={{ color: 'primary.main' }}>
                {isDarkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
              </IconButton>
            </Tooltip>
            {fromAgentStudio ? (
              <Tooltip title="Back to Agent Studio" placement="right">
                <IconButton onClick={handleBackToAgentStudio} size="medium" sx={{ color: 'primary.main' }}>
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Select Agents" placement="right">
                <IconButton onClick={onSelectAgents} size="medium" sx={{ color: 'primary.main' }}>
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Logout" placement="right">
              <IconButton onClick={onLogout} size="medium" sx={{ color: 'error.main' }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <>

            <Accordion
              expanded={expandedSection === 'history'}
              onChange={() => handleSectionToggle('history')}
              sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { margin: '10px 0' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <History fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    Chat History
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {sessions.length > 0 && !loadingSessions && (
                    <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                      {sessionSelectionMode ? (
                        <>
                          <Chip 
                            label={`${selectedSessions.length} selected`}
                            size="small"
                            color="primary"
                            sx={{ fontSize: '0.75rem' }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={selectAllSessions}
                            disabled={selectedSessions.length === sessions.length}
                          >
                            <Tooltip title="Select All">
                              <Add fontSize="small" />
                            </Tooltip>
                          </IconButton>
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick('sessions')}
                            disabled={selectedSessions.length === 0}
                            sx={{ height: 28, fontSize: '0.75rem', minWidth: 60 }}
                          >
                            Delete
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSessionSelectionMode(false);
                              setSelectedSessions([]);
                            }}
                            sx={{ height: 28, fontSize: '0.75rem', minWidth: 60 }}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outlined"
                          startIcon={<Delete fontSize="small" />}
                          onClick={() => setSessionSelectionMode(true)}
                          fullWidth
                          size="small"
                          sx={{ height: 32, fontSize: '0.85rem' }}
                        >
                          Select Sessions
                        </Button>
                      )}
                    </Box>
                  )}
                  {loadingSessions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : sessions.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        No chat history yet
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: isAdmin ? 300 : '100%', overflow: 'auto' }}>
                      <List sx={{ p: 0 }}>
                      {sessions.map((session) => (
                          <ListItem 
                            key={session.session_id} 
                            disablePadding
                            secondaryAction={
                              !sessionSelectionMode && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRenameClick(session);
                                    }}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  {session.session_id !== currentSessionId && (
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteClick('sessions', session.session_id);
                                      }}
                                      size="small"
                                      color="error"
                                      sx={{ p: 0.5 }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              )
                            }
                          >
                          <ListItemButton
                            onClick={() => {
                                if (sessionSelectionMode) {
                                  toggleSessionSelection(session.session_id);
                                } else {
                              onSessionSelect(session.session_id);
                                }
                            }}
                            selected={session.session_id === currentSessionId}
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              py: 0.8,
                              minHeight: 42,
                              '&.Mui-selected': {
                                bgcolor: 'primary.lighter',
                              }
                            }}
                          >
                              {sessionSelectionMode && (
                                <Checkbox
                                  checked={selectedSessions.includes(session.session_id)}
                                  size="small"
                                  sx={{ p: 0, mr: 1 }}
                                />
                              )}
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <ChatBubble fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={session.title || 'Chat'}
                              secondary={formatDate(session.created_at)}
                              primaryTypographyProps={{
                                variant: 'body2',
                                noWrap: true,
                                fontSize: '0.9rem'
                              }}
                              secondaryTypographyProps={{
                                variant: 'caption',
                                fontSize: '0.8rem'
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                      {hasMoreSessions && !isLoadingMoreSessions && sessions.length > 0 && (
                        <Button
                          onClick={() => loadSessions(sessionPage + 1, false)}
                          fullWidth
                          size="small"
                          sx={{ mt: 0.5, fontSize: '0.8rem' }}
                        >
                          Load More
                        </Button>
                      )}
                      {isLoadingMoreSessions && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                          <CircularProgress size={16} />
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {isAdmin && (
            <Accordion
              expanded={expandedSection === 'documents'}
              onChange={() => handleSectionToggle('documents')}
              sx={{
                boxShadow: 'none',
                  '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { margin: '10px 0' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Storage fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    Documents
                  </Typography>
                  <Badge badgeContent={totalDocCount} color="primary" sx={{ ml: 'auto' }}>
                    <Box sx={{ width: 14, height: 14 }} />
                  </Badge>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  <Box sx={{ display: 'flex', gap: 0.8 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AttachFile fontSize="small" />}
                      onClick={() => fileInputRef.current?.click()}
                      fullWidth
                      size="small"
                      disabled={uploadLoading || documentSelectionMode}
                      sx={{ height: 36, fontSize: '0.85rem' }}
                    >
                      Upload
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Link fontSize="small" />}
                      onClick={() => setCrawlDialogOpen(true)}
                      size="small"
                      disabled={crawlLoading || documentSelectionMode}
                      sx={{ height: 36, fontSize: '0.85rem', minWidth: 70 }}
                    >
                      Crawl
                    </Button>
                  </Box>
                  
                  {documents.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                      {documentSelectionMode ? (
                        <>
                          <Chip 
                            label={`${selectedDocuments.length} selected`}
                            size="small"
                            color="primary"
                            sx={{ fontSize: '0.75rem' }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={selectAllDocuments}
                            disabled={selectedDocuments.length === documents.length}
                          >
                            <Tooltip title="Select All">
                              <Add fontSize="small" />
                            </Tooltip>
                          </IconButton>
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick('documents')}
                            disabled={selectedDocuments.length === 0}
                            sx={{ height: 28, fontSize: '0.75rem', minWidth: 60 }}
                          >
                            Delete
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setDocumentSelectionMode(false);
                              setSelectedDocuments([]);
                            }}
                            sx={{ height: 28, fontSize: '0.75rem', minWidth: 60 }}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outlined"
                          startIcon={<Delete fontSize="small" />}
                          onClick={() => setDocumentSelectionMode(true)}
                          fullWidth
                          size="small"
                          sx={{ height: 32, fontSize: '0.85rem' }}
                        >
                          Select Documents
                        </Button>
                      )}
                    </Box>
                  )}
                  
                  {uploadLoading && documents.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 0.8 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}

                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <List sx={{ p: 0 }}>
                    {documents.map((doc) => (
                      <ListItem
                        key={doc.id}
                        sx={{
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          mb: 0.5,
                          py: 0.8,
                          border: '1px solid',
                            borderColor: 'divider',
                            cursor: documentSelectionMode ? 'pointer' : 'default'
                        }}
                          onClick={() => documentSelectionMode && toggleDocumentSelection(doc.id)}
                        secondaryAction={
                            !documentSelectionMode && (
                          <IconButton
                                onClick={() => handleDeleteClick('documents', doc.id)}
                            size="small"
                            color="error"
                            sx={{ p: 0.5 }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                            )
                        }
                      >
                          {documentSelectionMode && (
                            <Checkbox
                              checked={selectedDocuments.includes(doc.id)}
                              size="small"
                              sx={{ p: 0, mr: 1 }}
                            />
                          )}
                        <ListItemIcon sx={{ minWidth: 28 }}>
                            {(doc.status === 'preprocessing' || doc.status === 'indexing' || doc.status === 'processing') ? (
                              <CircularProgress size={16} thickness={4} sx={{ color: 'primary.main' }} />
                            ) : (
                          <Folder fontSize="small" />
                            )}
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.name || doc.filename}
                          secondary={doc.status || 'Unknown'}
                          primaryTypographyProps={{
                            variant: 'body2',
                            noWrap: true,
                            fontSize: '0.9rem'
                          }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                              fontSize: '0.8rem',
                              color: (doc.status === 'preprocessing' || doc.status === 'indexing' || doc.status === 'processing') ? 'primary.main' : 'text.secondary',
                              fontWeight: (doc.status === 'preprocessing' || doc.status === 'indexing' || doc.status === 'processing') ? 500 : 400
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                    {hasMorePages && !isLoadingMoreDocs && documents.length > 0 && (
                      <Button
                        onClick={() => onLoadMore && onLoadMore()}
                        fullWidth
                        size="small"
                        sx={{ mt: 0.5, fontSize: '0.8rem' }}
                      >
                        Load More Documents
                      </Button>
                    )}
                    {isLoadingMoreDocs && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={16} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            )}

            {isAdmin && (
            <Accordion
              expanded={expandedSection === 'plugins'}
              onChange={() => handleSectionToggle('plugins')}
              sx={{
                boxShadow: 'none',
                  '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { margin: '10px 0' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Extension fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    Integrations
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.8, fontSize: '0.9rem' }}>
                    Connect external services
                  </Typography>
                  
                  <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                    {[
                      { name: 'LinkedIn', icon: <LinkedIn fontSize="small" />, category: 'Social' },
                      { name: 'Slack', icon: <Message fontSize="small" />, category: 'Communication' },
                      { name: 'Jira', icon: <BugReport fontSize="small" />, category: 'Project' },
                      { name: 'Drive', icon: <CloudQueue fontSize="small" />, category: 'Storage' },
                      { name: 'Notion', icon: <Storage fontSize="small" />, category: 'Knowledge' },
                      { name: 'MySQL', icon: <Dashboard fontSize="small" />, category: 'Database' },
                      { name: 'PostgreSQL', icon: <Dashboard fontSize="small" />, category: 'Database' },
                      { name: 'MongoDB', icon: <Dashboard fontSize="small" />, category: 'Database' }
                    ].map((plugin) => (
                      <ListItem
                        key={plugin.name}
                        sx={{
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          mb: 0.5,
                          py: 0.8,
                          opacity: 0.6,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {plugin.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={plugin.name}
                          secondary={plugin.category}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontSize: '0.9rem'
                          }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            fontSize: '0.8rem'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </AccordionDetails>
            </Accordion>
            )}
          </>
        )}
      </Box>

      {!isCollapsed && (
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            startIcon={isDarkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
            onClick={toggleTheme}
            fullWidth
            size="small"
            color="primary"
            sx={{ height: 36, fontSize: '0.9rem', mb: 1 }}
          >
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </Button>
          {fromAgentStudio ? (
            <Button
              variant="outlined"
              startIcon={<ArrowBack fontSize="small" />}
              onClick={handleBackToAgentStudio}
              fullWidth
              size="small"
              color="primary"
              sx={{ height: 36, fontSize: '0.9rem', mb: 1 }}
            >
              Back to Agent Studio
            </Button>
          ) : (
          <Button
            variant="outlined"
            startIcon={<Settings fontSize="small" />}
              onClick={onSelectAgents}
            fullWidth
            size="small"
            color="primary"
            sx={{ height: 36, fontSize: '0.9rem', mb: 1 }}
          >
              Select Agents
          </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Logout fontSize="small" />}
            onClick={onLogout}
            fullWidth
            size="small"
            color="error"
            sx={{ height: 36, fontSize: '0.9rem' }}
          >
            Logout
          </Button>
        </Box>
      )}

      <Dialog
        open={crawlDialogOpen}
        onClose={() => setCrawlDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.1rem' }}>Crawl Website</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={crawlUrlInput}
            onChange={(e) => setCrawlUrlInput(e.target.value)}
            placeholder="https://example.com"
            size="small"
            sx={{ mb: 2, fontSize: '0.9rem' }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Max Pages"
              type="number"
              size="small"
              value={crawlOptions.maxPage}
              onChange={(e) => setCrawlOptions(prev => ({ ...prev, maxPage: parseInt(e.target.value) || 100 }))}
              inputProps={{ min: 1, max: 1000 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Max Depth"
              type="number"
              size="small"
              value={crawlOptions.maxDepth}
              onChange={(e) => setCrawlOptions(prev => ({ ...prev, maxDepth: parseInt(e.target.value) || 3 }))}
              inputProps={{ min: 1, max: 10 }}
              sx={{ flex: 1 }}
            />
          </Box>
          
          <FormControlLabel
            control={
              <Switch
                checked={crawlOptions.crawlSubDomain}
                onChange={(e) => setCrawlOptions(prev => ({ ...prev, crawlSubDomain: e.target.checked }))}
                size="small"
              />
            }
            label={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Crawl Subdomains</Typography>}
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={crawlOptions.fetchFiles}
                onChange={(e) => setCrawlOptions(prev => ({ ...prev, fetchFiles: e.target.checked }))}
                size="small"
              />
            }
            label={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>Fetch Files (PDFs, docs, etc.)</Typography>}
            sx={{ mb: 1 }}
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {crawlStatus}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrawlDialogOpen(false)} size="small" sx={{ fontSize: '0.85rem' }}>Cancel</Button>
          <Button
            onClick={() => {
              onCrawl(crawlOptions);
              setCrawlDialogOpen(false);
            }}
            disabled={crawlLoading || !crawlUrlInput.trim()}
            variant="contained"
            size="small"
            sx={{ fontSize: '0.85rem' }}
          >
            {crawlLoading ? 'Crawling...' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>

      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileUpload}
        style={{ display: 'none' }}
        multiple
        accept=".pdf,.doc,.docx,.txt,.md,.json,.csv,.xlsx,.pptx"
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title={deleteTarget?.type === 'sessions' ? 'Delete Sessions' : 'Delete Documents'}
        message={getDeleteMessage()}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleting}
      />

      <Dialog open={renameDialogOpen} onClose={handleRenameCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name"
            fullWidth
            variant="outlined"
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            placeholder="Enter session name"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel}>Cancel</Button>
          <Button 
            onClick={handleRenameConfirm} 
            variant="contained"
            disabled={!newSessionName.trim() || renamingSession}
          >
            {renamingSession ? 'Renaming...' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sidebar; 